import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Globe } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useTokens, useConfig } from "../../lib/queries";
import { useWallet } from "../../lib/wallet";
import { fmtAmt, fmtUsd, fmtPrice, dealLink } from "../../lib/format";
import { SlippageSaved } from "./SlippageSaved";

const PrivateToggle = ({ isPrivate, setIsPrivate }) => (
  <div className="panel px-3 py-2.5" data-testid="propose-private-box">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {isPrivate ? <Lock size={14} className="text-violet-300" /> : <Globe size={14} className="text-fog" />}
        <span className="text-xs font-medium text-white">Private deal (invite-only)</span>
      </div>
      <button
        role="switch"
        aria-checked={isPrivate}
        onClick={() => setIsPrivate((v) => !v)}
        data-testid="propose-private-toggle"
        className={`relative w-9 h-5 rounded-full transition-colors ${isPrivate ? "bg-violet" : "bg-line2"}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${isPrivate ? "left-4" : "left-0.5"}`} />
      </button>
    </div>
    <p className="text-[11px] text-fog mt-1.5 leading-relaxed">
      {isPrivate
        ? "Hidden from the public board. You'll get a private invite link to send to one counterparty. Settled trades still print on the public Trade History, but with both wallets shown as \"Private\"."
        : "Public listing — anyone on the Deal Board can take the other side."}
    </p>
  </div>
);

const TokenSideSelect = ({ tokens, symbol, setSymbol, side, setSide }) => (
  <div className="grid grid-cols-2 gap-3">
    <label className="block"><span className="eyebrow">Token · pair</span>
      <select value={symbol} onChange={(e) => setSymbol(e.target.value)} className="field mt-1" data-testid="propose-token-select">
        {(tokens || []).map((t) => <option key={t.symbol} value={t.symbol}>{`${t.symbol}/${t.stock} — ${t.name}`}</option>)}
      </select>
    </label>
    <div><span className="eyebrow">I want to</span>
      <div className="flex mt-1 bg-ink rounded border border-line2 p-0.5 h-9">
        {[["sell", "Sell (ask)"], ["buy", "Buy (bid)"]].map(([id, l]) => (
          <button key={id} onClick={() => setSide(id)} data-testid={`propose-side-${id}`} className={`flex-1 rounded-sm text-xs font-medium transition-colors ${side === id ? (id === "sell" ? "bg-neg/80 text-white" : "bg-pos/80 text-ink") : "text-fog hover:text-white"}`}>{l}</button>
        ))}
      </div>
    </div>
  </div>
);

const PoolInfo = ({ token }) => (
  <div className="panel px-3 py-2 mono text-[11px] text-fog flex justify-between">
    <span>pool {fmtUsd(token.pool_depth_usd)}</span><span>price {fmtPrice(token.pool_price_usd)}/M</span><span>reserve {fmtAmt(token.token_reserve)}</span>
  </div>
);

const SizeField = ({ token, amount, setAmount, onShare }) => (
  <label className="block"><span className="eyebrow">Size · {token?.symbol}</span>
    <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="any" placeholder="e.g. 50000000" className="field mt-1" data-testid="propose-amount-input" />
    <div className="flex gap-1 mt-1.5 flex-wrap">{[5, 10, 20, 30].map((p) => <button key={p} onClick={() => onShare(p)} className="tag border-line2 text-fog hover:border-violet hover:text-white max-lg:h-8 max-lg:px-3 max-lg:text-[11px]" data-testid={`propose-share-${p}`}>{p}% of pool</button>)}</div>
  </label>
);

const PriceField = ({ token, priceM, setPriceM, onDiscount, disc }) => (
  <label className="block"><span className="eyebrow">Price · USD per 1M tokens</span>
    <input value={priceM} onChange={(e) => setPriceM(e.target.value)} type="number" min="0" step="any" placeholder={token ? (token.pool_price_usd * 1e6).toFixed(2) : ""} className="field mt-1" data-testid="propose-price-input" />
    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
      {[0, 2, 5, 8].map((p) => <button key={p} onClick={() => onDiscount(p)} className="tag border-line2 text-fog hover:border-violet hover:text-white max-lg:h-8 max-lg:px-3 max-lg:text-[11px]" data-testid={`propose-discount-${p}`}>{p === 0 ? "at pool" : `−${p}%`}</button>)}
      {disc != null && <span className={`mono text-[11px] ml-auto ${disc > 0 ? "text-pos" : "text-neg"}`} data-testid="propose-discount-readout">{disc > 0 ? "−" : "+"}{Math.abs(disc).toFixed(2)}% vs pool</span>}
    </div>
  </label>
);

const NoteField = ({ note, setNote }) => (
  <label className="block"><span className="eyebrow">Note to counterparties (optional)</span>
    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={240} placeholder="Firm price? Timeline? Anything a counterparty should know." className="field mt-1 h-auto py-2 font-sans" data-testid="propose-note-input" />
  </label>
);

const DealSummary = ({ cfg, notional, fee }) => (
  <div className="panel px-3 divide-y divide-line/60">
    <div className="flex justify-between py-1.5 text-xs"><span className="text-slate-400">Notional</span><span className="mono text-slate-100" data-testid="propose-notional">{fmtUsd(notional)}</span></div>
    <div className="flex justify-between py-1.5 text-xs"><span className="text-slate-400">Settlement fee {cfg ? cfg.fee_bps / 100 : ""}% · in $CROSS (buyer, on settle)</span><span className="mono text-slate-100" data-testid="propose-fee">{fmtAmt(fee)} $CROSS</span></div>
    <div className="flex justify-between py-1.5 text-xs"><span className="text-slate-400">Expires if untaken</span><span className="mono text-slate-100">{cfg?.deal_expiry_hours ?? 72}h</span></div>
  </div>
);

const SubmitBar = ({ connected, valid, pending, side, isPrivate, onSubmit }) => (
  <div className="border-t border-line p-4">
    {!connected ? <div className="text-xs text-slate-400" data-testid="propose-connect-hint">Connect a wallet to post a listing. Posting requires a signature; no funds move until you fund your side.</div>
      : <button onClick={onSubmit} disabled={!valid || pending} className="btn-primary w-full" data-testid="propose-submit-btn">{pending ? "Awaiting signature…" : `Post ${isPrivate ? "private " : ""}${side === "sell" ? "ask" : "bid"} · sign to confirm`}</button>}
  </div>
);

export const ProposeDeal = ({ onCreated }) => {
  const w = useWallet();
  const qc = useQueryClient();
  const { data: tokens } = useTokens();
  const { data: cfg } = useConfig();
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("sell");
  const [amount, setAmount] = useState("");
  const [priceM, setPriceM] = useState("");
  const [note, setNote] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [preview, setPreview] = useState(null);

  const token = useMemo(() => tokens?.find((t) => t.symbol === (symbol || tokens?.[0]?.symbol)), [tokens, symbol]);
  useEffect(() => { if (!symbol && tokens?.[0]) setSymbol(tokens[0].symbol); }, [tokens, symbol]);

  const amt = Number(amount), price = Number(priceM) / 1e6;
  const valid = token && amt > 0 && price > 0;

  useEffect(() => {
    if (!valid) { setPreview(null); return; }
    const t = setTimeout(() => api.slippage({ token_symbol: token.symbol, token_amount: amt, price_usd: price }).then(setPreview).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [valid, token, amt, price]);

  const setDiscount = (pct) => token && setPriceM((token.pool_price_usd * (1 - pct / 100) * 1e6).toFixed(2));
  const setShare = (pct) => token && setAmount(String(Math.round(token.token_reserve * pct / 100)));

  const create = useMutation({
    mutationFn: async () => api.createDeal({ ...(await w.signAction("create_deal", "new")), token_symbol: token.symbol, side, token_amount: amt, price_usd: price, note: note || null, is_private: isPrivate }),
    onSuccess: (d) => {
      qc.setQueryData(["deal", d.id], d);
      if (d.private) {
        const link = dealLink(d.id);
        navigator.clipboard?.writeText(link).catch(() => {});
        toast.success(`Private deal ${d.id} created — invite link copied.`, {
          description: "Send it to your counterparty so they can fund their side.",
          action: { label: "Copy link", onClick: () => navigator.clipboard?.writeText(link) },
          duration: 10000,
        });
      } else {
        toast.success(`Listing ${d.id} posted. Expires in ${cfg?.deal_expiry_hours}h if untaken.`);
      }
      qc.invalidateQueries();
      setAmount(""); setPriceM(""); setNote(""); setIsPrivate(false);
      onCreated?.(d);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const disc = token && price > 0 ? (1 - price / token.pool_price_usd) * 100 : null;
  const notional = valid ? amt * price : 0;
  const fee = cfg && notional ? (notional * cfg.fee_bps / 10000) / cfg.cross_price_usd : 0;

  return (
    <div className="flex flex-col h-full" data-testid="propose-deal-form">
      <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
        <TokenSideSelect tokens={tokens} symbol={symbol} setSymbol={setSymbol} side={side} setSide={setSide} />
        {token && <PoolInfo token={token} />}
        <SizeField token={token} amount={amount} setAmount={setAmount} onShare={setShare} />
        <PriceField token={token} priceM={priceM} setPriceM={setPriceM} onDiscount={setDiscount} disc={disc} />
        <NoteField note={note} setNote={setNote} />
        <PrivateToggle isPrivate={isPrivate} setIsPrivate={setIsPrivate} />
        {valid && <DealSummary cfg={cfg} notional={notional} fee={fee} />}
        {preview && <SlippageSaved s={preview} symbol={token.symbol} amount={amt} compact />}
      </div>

      <SubmitBar connected={w.connected} valid={valid} pending={create.isPending} side={side} isPrivate={isPrivate} onSubmit={create.mutate} />
    </div>
  );
};
